/**
 * @file care-tool-entry.schema.ts
 * @description Registros das ferramentas que não têm casa em outra coleção.
 *
 * Ciclo, dúvidas, medicações, mobilograma, contrações, plano pós-parto, peso do
 * bebê e recordatório vivem aqui. Pressão, glicemia, peso, exames e plano de
 * parto **não** — cada um já tem seu lugar, e duplicá-los faria o motor ler de
 * um lado enquanto a ferramenta escreve do outro. Ver `care-tool.schema.ts`.
 *
 * ## Por que `payload` é permissivo
 *
 * O formato de cada `payload` varia por ferramenta e vai mudar conforme as
 * telas evoluírem. Se o schema armazenado descrevesse cada formato, um campo
 * novo obrigatório derrubaria a listagem inteira — `findMany` lança no primeiro
 * documento inválido, e o registro antigo continua no disco como estava.
 *
 * A validação estrita existe, mas na **escrita**: `care-tool.service` valida o
 * payload contra o schema da ferramenta antes de gravar. Leitura permissiva,
 * escrita estrita — é o mesmo expand/contract das outras coleções, aplicado
 * dentro do documento.
 */

import { z } from 'zod';

export const CareToolEntrySchema = z.object({
  id: z.string(),
  userId: z.string(),
  /**
   * Marca da sessão em que o registro aconteceu (A052, fase 2). Opcional
   * enquanto o dado antigo é backfillado; a leitura passa a filtrar por ele
   * na fase 3. Cada CRM vê só o que aconteceu na própria marca.
   */
  brandId: z.string().nullable().optional(),
  /** Chave do catálogo. Não é enum: ferramenta nova não pode invalidar histórico. */
  toolKey: z.string(),
  /**
   * Quando o fato aconteceu, que não é quando foi digitado. Ela registra hoje o
   * peso de ontem; o gráfico e as variáveis seguem este campo.
   */
  occurredAt: z.date(),
  payload: z.record(z.any()).default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().default(null),
});

export type CareToolEntry = z.infer<typeof CareToolEntrySchema>;

// ---------------------------------------------------------------------------
// Payloads por ferramenta — usados só na escrita
// ---------------------------------------------------------------------------

const CicloPayload = z.object({
  /** Duração do fluxo em dias, quando ela souber informar. */
  duracaoFluxoDias: z.number().int().min(1).max(15).nullable().optional(),
  fluxoIntenso: z.boolean().optional(),
  observacao: z.string().max(500).optional(),
});

const DuvidaPayload = z.object({
  pergunta: z.string().min(1).max(500),
  respondida: z.boolean().default(false),
});

const MedicacaoPayload = z.object({
  nome: z.string().min(1).max(120),
  dose: z.string().max(60).optional(),
  frequencia: z.string().max(60).optional(),
  horario: z.string().max(20).optional(),
  continua: z.boolean().default(false),
});

const MobilogramaPayload = z.object({
  movimentos: z.number().int().min(0).max(200),
  duracaoMinutos: z.number().int().min(1).max(240),
  /** Sessão encerrada por atingir a meta, ou pelo tempo acabar. */
  atingiuMeta: z.boolean().default(false),
});

const ContracoesPayload = z.object({
  contracoes: z
    .array(
      z.object({
        inicio: z.coerce.date(),
        duracaoSegundos: z.number().int().min(1).max(600),
      })
    )
    .min(1),
  duracaoMediaSegundos: z.number().min(0),
  intervaloMedioSegundos: z.number().min(0).nullable(),
});

const PlanoPosPartoPayload = z.object({
  redeDeApoio: z
    .array(z.object({ nome: z.string().min(1).max(120), papel: z.string().max(60).optional() }))
    .default([]),
  quemAjudaNaCasa: z.string().max(500).optional(),
  amamentacao: z.string().max(500).optional(),
  visitas: z.string().max(500).optional(),
  sinaisDeAlerta: z.string().max(500).optional(),
});

const PesoBebePayload = z.object({
  pesoGramas: z.number().int().min(300).max(30000),
  observacao: z.string().max(300).optional(),
});

const RecordatorioPayload = z.object({
  refeicao: z.enum(['CAFE', 'LANCHE_MANHA', 'ALMOCO', 'LANCHE_TARDE', 'JANTAR', 'CEIA']),
  descricao: z.string().min(1).max(1000),
});

/** Diário e carta ao bebê (A121): texto livre, privado. O teto só barra abuso. */
/**
 * Os pontos de partida do diário (A131, especificação do time clínico): só
 * um convite para começar — nenhum é obrigatório, e "Escrever livremente"
 * existe para quem não quer escolher. `CARTA` é a "Carta para o meu bebê",
 * a opção especial que mora dentro do diário.
 */
export const PONTOS_DE_PARTIDA_DO_DIARIO = [
  { chave: 'SENTINDO', rotulo: 'Como estou me sentindo', emoji: '💗' },
  { chave: 'BEBE', rotulo: 'Sobre meu bebê', emoji: '🕊️' },
  { chave: 'DIA', rotulo: 'Como foi meu dia', emoji: '🌿' },
  { chave: 'PENSANDO', rotulo: 'O que estou pensando', emoji: '💭' },
  { chave: 'LEMBRANCA', rotulo: 'Uma lembrança que quero guardar', emoji: '✨' },
  { chave: 'LIVRE', rotulo: 'Quero escrever livremente', emoji: '✍️' },
] as const;
export const PontoDePartidaDoDiarioEnum = z.enum(['SENTINDO', 'BEBE', 'DIA', 'PENSANDO', 'LEMBRANCA', 'LIVRE', 'CARTA']);
export type PontoDePartidaDoDiario = z.infer<typeof PontoDePartidaDoDiarioEnum>;
export const TITULO_DO_DIARIO_MAX = 120;

/** Referência a uma foto guardada em privado (A131): o id do arquivo, nunca um endereço. */
export const IdDeMidiaSchema = z.string().uuid();

/**
 * Entrada do diário. Só o texto é obrigatório; ponto de partida, título e
 * foto chegaram com a A131 como opcionais (expand) — entrada antiga, só com
 * o texto, continua válida.
 */
const DiarioPayload = z.object({
  texto: z.string().trim().min(1).max(10000),
  ponto: PontoDePartidaDoDiarioEnum.optional(),
  titulo: z.string().trim().max(TITULO_DO_DIARIO_MAX).optional(),
  foto: IdDeMidiaSchema.optional(),
});

/**
 * Carta ao bebê da A121. Desde a A131 a carta mora dentro do diário (entrada
 * com `ponto: 'CARTA'`); as cartas gravadas antes continuam aqui e abrem no
 * diário — título e foto opcionais para poderem ser editadas como as outras.
 */
const CartaAoBebePayload = z.object({
  carta: z.string().trim().min(1).max(10000),
  titulo: z.string().trim().max(TITULO_DO_DIARIO_MAX).optional(),
  foto: IdDeMidiaSchema.optional(),
});

/**
 * Memórias do bebê (A132): cinco tipos de registro, cada um com o que a
 * especificação pede. Tudo opcional além do essencial de cada tipo — a foto
 * na foto, a imagem no ultrassom, o marco na data, título e texto no
 * detalhe. "Sobre o bebê" é um só por usuária (a API sobrescreve).
 *
 * Sem `tipo` e com `texto`, vira um detalhe: é o que o app antigo, que só
 * tem o formulário genérico, consegue gravar.
 */
export const MARCOS_DAS_MEMORIAS = [
  { chave: 'DESCOBERTA', rotulo: 'Descobri a gestação' },
  { chave: 'PRIMEIRO_ULTRASSOM', rotulo: 'Primeiro ultrassom' },
  { chave: 'CORACAO', rotulo: 'Ouvi o coração' },
  { chave: 'MOVIMENTOS', rotulo: 'Senti os primeiros movimentos' },
  { chave: 'NOME', rotulo: 'Escolhemos o nome' },
  { chave: 'NASCIMENTO', rotulo: 'Nascimento' },
  { chave: 'OUTRA', rotulo: 'Outra' },
] as const;
export const MarcoDasMemoriasEnum = z.enum(['DESCOBERTA', 'PRIMEIRO_ULTRASSOM', 'CORACAO', 'MOVIMENTOS', 'NOME', 'NASCIMENTO', 'OUTRA']);
export type MarcoDasMemorias = z.infer<typeof MarcoDasMemoriasEnum>;

const textoCurto = (max: number) => z.string().trim().max(max).optional();
const DiaSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const MemoriasPayload = z.preprocess(
  (bruto) => {
    const v = bruto as Record<string, unknown> | null;
    return v && typeof v === 'object' && !v.tipo && typeof v.texto === 'string' ? { tipo: 'DETALHE', titulo: 'Um detalhe', ...v } : bruto;
  },
  z.discriminatedUnion('tipo', [
    z.object({
      tipo: z.literal('SOBRE'),
      nome: textoCurto(80),
      apelido: textoCurto(80),
      nascimento: DiaSchema.optional(),
      semanas: z.number().int().min(4).max(45).optional(),
      pesoGramas: z.number().int().min(1).max(7000).optional(),
      comprimentoCm: z.number().min(1).max(70).optional(),
      outros: textoCurto(1000),
    }),
    z.object({ tipo: z.literal('FOTO'), foto: IdDeMidiaSchema, legenda: textoCurto(300) }),
    z.object({ tipo: z.literal('ULTRASSOM'), imagem: IdDeMidiaSchema, semanas: z.number().int().min(4).max(45).optional(), descricao: textoCurto(300) }),
    z.object({ tipo: z.literal('DATA'), marco: MarcoDasMemoriasEnum, outro: textoCurto(80), descricao: textoCurto(300) }),
    z.object({ tipo: z.literal('DETALHE'), titulo: z.string().trim().min(1).max(80), texto: z.string().trim().min(1).max(2000), contexto: textoCurto(300) }),
  ]).superRefine((d, ctx) => {
    // "Outra" precisa dizer o quê: sem isso a linha do tempo mostraria só "Outra".
    if (d.tipo === 'DATA' && d.marco === 'OUTRA' && !d.outro?.trim()) ctx.addIssue({ code: 'custom', message: 'Conte o que aconteceu nesse dia.', path: ['outro'] });
  }),
);
export type TipoDeMemoria = 'SOBRE' | 'FOTO' | 'ULTRASSOM' | 'DATA' | 'DETALHE';

/**
 * Mês de cura (A133, antes "31 dias de cuidado"): um registro por dia que ela marcou — fez, e se
 * fez bem. A API guarda um só por dia (marcar de novo atualiza).
 */
export const DIAS_DE_CUIDADO = 31;
const TrintaDiasPayload = z.object({
  dia: z.number().int().min(1).max(DIAS_DE_CUIDADO),
  feito: z.boolean().optional(),
  favorito: z.boolean().optional(),
});

/**
 * Só as ferramentas que gravam em `careToolEntries` aparecem aqui. Chave fora
 * deste mapa é recusada na escrita — é o que impede um cliente de usar a
 * coleção como depósito genérico.
 */
export const CARE_TOOL_ENTRY_PAYLOADS: Record<string, z.ZodTypeAny> = {
  diario: DiarioPayload,
  carta_ao_bebe: CartaAoBebePayload,
  memorias_bebe: MemoriasPayload as unknown as z.ZodTypeAny,
  trinta_dias: TrintaDiasPayload,
  monitoramento_ciclo: CicloPayload,
  minhas_duvidas: DuvidaPayload,
  lembrete_medicacoes: MedicacaoPayload,
  mobilograma: MobilogramaPayload,
  contador_contracoes: ContracoesPayload,
  plano_pos_parto: PlanoPosPartoPayload,
  peso_bebe: PesoBebePayload,
  recordatorio_alimentar: RecordatorioPayload,
};
